import { Component, OnInit, forwardRef, Input, ViewChild, Output, EventEmitter, SimpleChanges, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { 
    ToastController, IonIcon
} from '@ionic/angular/standalone';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { environment } from '@app/environment';
import { 
    DemoozModalMediaComponent, DemoozMessageComponent, DemoozTextareaComponent, DemoozCheckboxComponent, 
    DemoozSelectComponent, DemoozSelectItem, DemoozInputFileComponent, DemoozInputFile, DemoozInputComponent, DemoozButtonComponent 
} from '@demooz/components';
import { DataService, ValidatorsService, ResponseService, CloudinaryService, AmbassadorService } from '@app/services';
import { Challenge, ResponseChallengeStatus, ResponseChallenge, Match } from '@app/models';

class ChallengeMedia {
    src: string;
    type: 'picture'|'video';
    constructor(src: string, type: 'picture'|'video'){
        this.src = src;
        this.type = type;
    }
}

class ChallengeOptinalMedia {
    file?: File;
    type?: 'media'|'video';
    url?: string;
}

const MaxSizeFile: any = {media: 10485760, video: 1073741824};
const ValidFileExtensions: any = {media: ['jpg', 'png', 'jpeg', 'webp', 'pdf'], video: ['mp4', 'mov']};
const ValidDataTypes: any = {media: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'], video: [ 'video/mp4', 'video/quicktime']};
    
@Component({
    selector: 'app-challenge-response',
    templateUrl: './response.component.html',
    styleUrls: ['./response.component.scss'],
    imports: [
        IonIcon, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => DemoozModalMediaComponent), forwardRef(() => DemoozMessageComponent), forwardRef(() => DemoozTextareaComponent),
        forwardRef(() => DemoozButtonComponent), forwardRef(() => DemoozInputFileComponent), forwardRef(() => DemoozCheckboxComponent),
        forwardRef(() => DemoozSelectComponent), forwardRef(() => DemoozInputComponent)
    ]
})
export class ChallengesResponseComponent implements OnInit {
      
    @Input({alias : 'challenge', required : true}) challenge: Challenge;
    @Input({alias : 'match', required : false}) match: Match;
    @Input({alias : 'externalLink', required : false}) externalLink: boolean = false;
    @Output() onSubmitResponse = new EventEmitter<boolean>();
    
    @ViewChild('challengeResponseContent') challengeResponseContent: ElementRef;
    @ViewChild('modalMedia') modalMedia: DemoozModalMediaComponent;
    @ViewChild('optionalMediaInput') optionalMediaInput: DemoozInputFileComponent;
    
    environment = environment;
    settings: any = this.dataService.operation.settings;
    salesContent: any = this.dataService.operation.salesContent;
    responseChallenge: ResponseChallenge|null = null;
    responseChallengeStatus: ResponseChallengeStatus = new ResponseChallengeStatus();
    description: SafeHtml;
    levelLabel: string = '';
    incentivePointsLabel: SafeHtml;
    incentiveLabel: SafeHtml;    
    lostIncentives: boolean = false;
    descriptionLink: SafeHtml;
    challengeMedia: ChallengeMedia|null = null;
    isShareSupported: boolean = false;
    nbSalesResponses: number = 0;    
    canPostResponse: boolean = false;
    canEditResponse: boolean = false;
    disabled: boolean = false;
    isFetchingSubmit: boolean = false;
    responsesTypesOptions: Array<DemoozSelectItem> = [];
    acceptFiles: Array<string> = [];
    infosFile: string = '';
    proofFile: DemoozInputFile|null = null;
    optionalsMedias: Array<ChallengeOptinalMedia> = [];
    acceptFilesOptionalsMedias: Array<string> = [];
    responseForm: FormGroup = new FormGroup({});
    errorFile: string|null = null;
    errorText: string|null = null;
    changeResponseType: Subscription;
    matchsDemonstrationsValidated: Array<DemoozSelectItem> = [];
    
    constructor(     
        private domSanitizer: DomSanitizer,
        public translateService: TranslateService,
        private toastController: ToastController,
        private cloudinaryService: CloudinaryService,
        public dataService: DataService,
        public ambassadorService: AmbassadorService,
        private responseService: ResponseService
    ) { }

    ngOnInit() {        
        this.isShareSupported = (typeof navigator.share === 'function');        
    }
    
    async ngOnChanges(changes: SimpleChanges) {        
        if((changes['challenge'] && changes['challenge'].currentValue) || (changes['match'] && changes['match'].currentValue)){
            this.initChallenge();
        }
    }
    
    ngOnDestroy() {  
        if(this.changeResponseType){
            this.changeResponseType.unsubscribe();
        }
    }
   
    initChallenge(){
        
        this.responseForm = new FormGroup({});
        this.canPostResponse = (['external','demonstration'].indexOf(this.challenge.type) === -1);
        this.canEditResponse = false;
        
        this.responseChallenge = this.challenge.response || null;        
        this.matchsDemonstrationsValidated = [];
        this.nbSalesResponses = 0;
        this.optionalsMedias = [];        
        this.acceptFilesOptionalsMedias = [];        
       
        if(this.match && this.challenge.type === environment.CHALLENGE_TYPE.SALE){            
            this.nbSalesResponses = this.match.nbSales;
            this.checkNbSales();            
        }
        
        if(this.responseChallenge){
            this.disabled = true;
        }
        
        if(this.canPostResponse
            && this.responseChallenge
            && (this.responseChallenge.status === environment.CHALLENGE_VALIDATION_TYPE.AWAITING_VALIDATION 
                || (this.responseChallenge.status === environment.CHALLENGE_VALIDATION_TYPE.DENIED && this.challenge.type !== environment.CHALLENGE_TYPE.SALE)) 
            ){
            this.canEditResponse = true;
        }
        
        let description = (this.challenge.description && this.challenge.description !== '' ? this.translateService.instant(this.challenge.description) : '');
        this.description = this.domSanitizer.bypassSecurityTrustHtml(description);
        this.responseChallengeStatus = ResponseChallengeStatus.FromStatus(this.responseChallenge?.status, this.challenge.type);
        
        let levelLabel = '';
        if(this.challenge.level === 'missions'){
            levelLabel = this.translateService.instant('CHALLENGE.LEVEL_MISSION');
        }
        else if (this.challenge.level === 'flash'){
            levelLabel = this.translateService.instant('CHALLENGE.LEVEL_FLASH');
        }
        else if(!isNaN(parseInt(this.challenge.level))){
            let level = this.dataService.operation.levels[this.challenge.level];
            levelLabel = (level ? level.name : '');
        }
        this.levelLabel = levelLabel;
        
        let nbPointsLabel = '';
        let incentiveLabel = '';
        if(this.responseChallenge && this.responseChallenge.status === environment.CHALLENGE_VALIDATION_TYPE.VALIDATED){
            if(this.responseChallenge.points > 0){
                nbPointsLabel = this.translateService.instant('CHALLENGE.POINTS_EARNED').replace('{nb_points}', this.responseChallenge.points); 
                if(this.responseChallenge.incentive){
                    incentiveLabel = '+<b>'+this.responseChallenge.incentive+'</b> '+this.responseChallenge.devise;
                }
            }
        }
        else{
            if(this.challenge.points > 0){
                nbPointsLabel = this.translateService.instant('CHALLENGE.POINTS_TO_WIN').replace('{nb_points}', this.challenge.points);    
                if(this.challenge.incentive){   
                    incentiveLabel = '+<b>'+this.challenge.incentive+'</b> '+this.challenge.devise;
                }
            }
        }
        this.incentivePointsLabel = (nbPointsLabel !== '' ? this.domSanitizer.bypassSecurityTrustHtml(nbPointsLabel) : '');
        this.incentiveLabel = (incentiveLabel !== '' ? this.domSanitizer.bypassSecurityTrustHtml(incentiveLabel) : '');
        
        this.lostIncentives = (this.challenge.category === 'history' && [environment.CHALLENGE_VALIDATION_TYPE.DENIED, environment.CHALLENGE_VALIDATION_TYPE.DENIED_DEFINITELY].indexOf(this.challenge.response?.status) !== -1);
                
        this.descriptionLink = '';
        if(this.challenge.descriptionLink && this.challenge.descriptionLink !== ''){
            this.descriptionLink = this.domSanitizer.bypassSecurityTrustHtml(this.challenge.descriptionLink);
        }
      
        let challengeMedia: ChallengeMedia|null = null;
        if(this.challenge.mediaLink && this.challenge.mediaLink != ''){
            let mediaType: 'picture'|'video'|null = null;;
            let temp = this.challenge.mediaLink.split('.');                
            if(temp.length > 0){
                let ext = temp[temp.length-1].toLowerCase();
                if(['mp4'].indexOf(ext) !== -1){
                    mediaType = 'video';
                }
                else if(['png','jpg', 'jpeg', 'gif'].indexOf(ext) !== -1){
                    mediaType = 'picture';
                }
            }  
            if(mediaType !== null){
                challengeMedia = new ChallengeMedia(this.challenge.mediaLink, mediaType);
            }
        }
        this.challengeMedia = challengeMedia;
        
        if(this.canPostResponse){
            
            this.responseForm.addControl('responseType', new FormControl(null,  [Validators.required]), {emitEvent: false}); 
            this.changeResponseType = this.responseForm.controls['responseType'].valueChanges.subscribe(value => {
               
                let typeResponse = this.responseForm.controls['responseType'].value;
                switch(typeResponse){
                    case 'media': case 'video':
                    
                        this.acceptFiles = ValidFileExtensions[typeResponse].map((e: string) => '.'+e);
                        this.infosFile = ' - Max '+this.displayOctets(MaxSizeFile[typeResponse])+' ('+this.acceptFiles.join(', ')+')';
                        if(typeof this.responseForm.controls['proof_file'] !== 'undefined'){
                            this.proofFile = null;
                            this.responseForm.controls['proof_file'].setValue(null, {emitEvent: false});
                        }
                        else{
                            this.responseForm.addControl('proof_file', new FormControl(null, [Validators.required]), {emitEvent: false}); 
                            this.responseForm.removeControl('proof', {emitEvent: false});
                        }
                        break;                        
                                                 
                    case 'url': case 'text': case 'longtext': 
                    
                        this.acceptFiles = [];
                        this.infosFile = '';
                        if(typeof this.responseForm.controls['proof'] !== 'undefined'){
                            this.responseForm.controls['proof'].setValue(null, {emitEvent: false});
                        }
                        else{
                            this.proofFile = null;
                            this.responseForm.addControl('proof', new FormControl(this.responseChallenge?.response || null, [Validators.required]), {emitEvent: false}); 
                            this.responseForm.removeControl('proof_file', {emitEvent: false});
                        }
                                                  
                        break;                        
                }  
                
                this.challengeResponseContent?.nativeElement.scrollTo({
                    top: this.challengeResponseContent.nativeElement.scrollHeight,
                    behavior: 'smooth'
                });              
            });
        
            if(this.challenge.commentEnabled){
                this.responseForm.addControl('comment', new FormControl(this.responseChallenge?.comment || null), {emitEvent: false}); 
            }
            
            if(this.challenge.checkboxEnabled && !this.disabled){
                this.responseForm.addControl('checkbox', new FormControl(false, [Validators.requiredTrue]), {emitEvent: false}); 
            }
            
            let responsesTypesOptions: Array<DemoozSelectItem> = [];
            let acceptFiles: Array<string> = [];        
            if(this.settings?.responsesTypes?.length > 0){
                let salesContent = this.dataService.operation.salesContent;
                this.settings.responsesTypes.forEach((r: any) => {
                    if(
                    (this.challenge.type === 'sale' && (!salesContent?.responseType || salesContent.responseType.length === 0 || salesContent.responseType.includes(r.key)))
                    || (this.challenge.type !== 'sale' && (!this.challenge.responseType || this.challenge.responseType.length === 0 || this.challenge.responseType.includes(r.key)))
                    ){
                        responsesTypesOptions.push(new DemoozSelectItem(r.name, r.key));                     
                    }
                });
            }        
            this.responsesTypesOptions = responsesTypesOptions;
            if(this.responseChallenge){
                let typeResponse = (this.responseChallenge.typeResponse && this.responsesTypesOptions.find(r => r.value === this.responseChallenge?.typeResponse) ? this.responseChallenge.typeResponse : null);
                this.responseForm.controls['responseType'].setValue(typeResponse);
            }
            else if(this.responsesTypesOptions.length === 1){
                this.responseForm.controls['responseType'].setValue(this.responsesTypesOptions[0].value);
            }
            
            if(this.challenge.optionalsMediasEnabled){
                this.challenge.optionalsMediasType.forEach((t) => {
                    this.acceptFilesOptionalsMedias = this.acceptFilesOptionalsMedias.concat(ValidFileExtensions[t].map((e: string) => '.'+e));
                });                
                this.responseForm.addControl('optional_media', new FormControl(null), {emitEvent: false}); 
                
                if(this.challenge.response?.optionalsMedias?.length > 0){
                    this.challenge.response.optionalsMedias.forEach(m => {
                        this.optionalsMedias.push({url: m});
                    });
                }
            }
            
            if(!this.externalLink && this.challenge.type === environment.CHALLENGE_TYPE.SALE){    
                let responseMatchID = this.challenge.response?.matchID;
                let matchsDemonstrationsValidated: Array<DemoozSelectItem> = [];
                this.dataService.ambassador.matchesValidated.forEach(m => {
                    if(m.nbSales < this.salesContent?.nbMaxSales || responseMatchID === m.ID){
                        let dateMatch = moment(new Date(parseInt(m._.created))).format('DD/MM/YYYY');
                        let testerName = m.tester.firstName+' '+m.tester.lastName+' - '+dateMatch;  
                        matchsDemonstrationsValidated.push(new DemoozSelectItem(testerName, m.ID));
                    }
                });
                this.matchsDemonstrationsValidated = matchsDemonstrationsValidated;
                this.responseForm.addControl('match', new FormControl(responseMatchID || null, [Validators.required]), {emitEvent: false});                 
            }
            
        }
       
    }
    
    checkNbSales(){
        if(this.nbSalesResponses >= this.salesContent?.nbMaxSales || this.match?.salesDeniedDefinitely){
            this.canPostResponse = false;
        }
    }
    
    displayOctets(octets: number|undefined): string{
        
        if(octets === undefined){
            return '';
        }
        
        let i = 0;
        let tabUnits = ['o', 'Ko', 'Mo', 'Go', 'To'];
        let newOctets = octets / 1024;
        let unit = tabUnits[i];
        while(newOctets >= 1 && (i+1) < tabUnits.length){
            i++;
            octets = newOctets;
            newOctets = octets / 1024;
            unit = tabUnits[i];        
        }
        return octets+' '+unit;
    }
    
    isValidFileType(file: File, fileType: string) {
        return (file 
            && (ValidFileExtensions[fileType]?.indexOf(file.name.toLowerCase().split('.').pop()) !== -1)
            && (ValidDataTypes[fileType]?.indexOf(file.type.toLowerCase()) !== -1 ) );
    }

    isValidFileSize(file: File, fileType: string) {
        return (file && file.size <= MaxSizeFile[fileType]);
    }
    
    getTypeFile(file: File): 'media'|'video'|undefined{
        let typeFile: 'media'|'video'|undefined = undefined;
        if(ValidDataTypes['media'].find((t: any) => t === file.type)){
            typeFile = 'media';
        }
        else if(ValidDataTypes['video'].find((t: any) => t === file.type)){
            typeFile = 'video';
        }
        return typeFile;
    }
    
    async shareSocials(){
        if(this.isShareSupported){
            let shareData = {
                title: 'Share',
                text: 'Share'
            };
            await navigator.share(shareData);
        }
    }
    
    async copyHashtagsToClipboard() {
        if(this.challenge.hashtags && this.challenge.hashtags.length > 0){
            let text = this.challenge.hashtags.join(' ');
            await navigator.clipboard.writeText(text);
            const toast = await this.toastController.create({
                message: this.translateService.instant('CHALLENGE_RESPONSE.MSG_SUCCESS_COPY'),
                duration: 3000,
                position: 'top',
                color: 'success'
            });
            await toast.present();   
        }
    }
    
    showMedia(){
        this.modalMedia.open(this.challenge.mediaLink);
    }
    
    openLinkChallenge(){
        if(this.challenge.link != ''){
            let link = document.createElement('a');
            link.href = this.challenge.link;
            link.target = '_blank';
            link.click();
        }
    }
    
    async submitResponse() {
        
        if(this.match && this.challenge.type === environment.CHALLENGE_TYPE.SALE
            && (this.nbSalesResponses >= this.salesContent?.nbMaxSales || this.match.salesDeniedDefinitely)){
            this.canPostResponse = false;                     
        }
        
        if(!this.disabled && this.canPostResponse){
            
            this.errorFile = null;
            this.errorText = null;
            
            if(this.responseForm.valid){  
                this.isFetchingSubmit = true;
                
                let responseValue: any = null;
                let typeResponse = this.responseForm.controls['responseType'].value;
                
                switch(typeResponse){
                    case 'media': case 'video':
                        if(this.proofFile !== null){
                            
                            let typeResponseInfos = this.settings?.responsesTypes.find((r: any) => r.key === typeResponse);
                            
                            if(!this.isValidFileType(this.proofFile.filedata, typeResponse)){
                                this.errorFile = typeResponseInfos.error;
                            }
                            else if(!this.isValidFileSize(this.proofFile.filedata, typeResponse)){
                                this.errorFile = typeResponseInfos.errorSize;                                
                            }
                            
                            if(this.errorFile === null){
                                let folder = 'ea-operations/'+this.dataService.operation.value.ID+'/challenges/'+this.challenge.ID+'/responses';
                                let typeFile: 'image'|'video' = (typeResponse === 'video' ? 'video' : 'image');
                                responseValue = await this.cloudinaryService.uploadFile(this.proofFile.filedata, folder, typeFile);
                            }
                            else{
                                this.responseForm.controls['proof_file'].setErrors({errorFile: true});
                            }
                        }
                        break;
                    case 'url': case 'text': case 'longtext': 
                         
                        let typeResponseInfos = this.settings?.responsesTypes.find((r: any) => r.key === typeResponse);
                        
                        let responseTextRegex: string|undefined = undefined;
                        let responseTextError: string = '';                        
                        if(this.challenge.type === environment.CHALLENGE_TYPE.SALE){
                            responseTextRegex = this.salesContent?.responseTextRegex;
                            responseTextError = this.salesContent?.responseTextError || '';
                        }
                        else{
                            responseTextRegex = this.challenge.responseTextRegex;
                            responseTextError = this.challenge.responseTextError || '';
                        }                        
                        
                        if(typeResponse === 'url'){
                            if(!ValidatorsService.urlRegex.test(this.responseForm.controls['proof'].value)){
                                this.errorText = typeResponseInfos.error;
                            }
                        }
                        else if(responseTextRegex && responseTextRegex != ''){
                            let regex = ValidatorsService.StringToRegex(responseTextRegex);
                            if(!regex.test(this.responseForm.controls['proof'].value)){
                                this.errorText = responseTextError;
                            }
                        }
                        
                        if(this.errorText === null){
                           responseValue = this.responseForm.controls['proof'].value;
                        }                    
                        else{
                            this.responseForm.controls['proof'].setErrors({errorProof: true});
                        }
                            
                        break;                        
                }
                
                let optionalsMedias: Array<string> = [];
                if(this.challenge.optionalsMediasEnabled
                    && this.optionalsMedias.length > 0){
                    for(let optionalMedia of this.optionalsMedias){                        
                        if(optionalMedia.file){                           
                            let typeFile: 'image'|'video'|'auto' = 'auto';                            
                            if(optionalMedia.type === 'media'){
                                typeFile = 'image';
                            } 
                            else if(optionalMedia.type === 'video'){
                                typeFile = 'video';
                            } 
                            let folder = 'ea-operations/'+this.dataService.operation.value.ID+'/challenges/'+this.challenge.ID+'/responses';                                  
                            let pathMedia = await this.cloudinaryService.uploadFile(optionalMedia.file, folder, typeFile);
                            if(pathMedia){
                                optionalsMedias.push(pathMedia);
                            }
                        }
                        else if(optionalMedia.url){
                            optionalsMedias.push(optionalMedia.url);
                        }
                    }
                }
                
                if(responseValue !== null && responseValue !== ''){
                    let comment = (this.challenge.commentEnabled ? this.responseForm.controls['comment'].value : null);
                    let response: any = null;
                    if(this.challenge.type === environment.CHALLENGE_TYPE.SALE){
                        let matchID: string = '';
                        let ownerID: string = '';
                        if(this.externalLink && this.match){
                            matchID = this.match.ID;
                            ownerID = this.match.ownerID;
                        }
                        else{
                            matchID = this.responseForm.controls['match'].value;
                            ownerID = this.dataService.ambassador.value.ID;
                        }
                        response = await this.responseService.postResponseSale(this.responseChallenge?.ID, ownerID, matchID, typeResponse, responseValue, comment, optionalsMedias);
                    }
                    else{
                        response = await this.responseService.postResponse(this.responseChallenge?.ID, this.challenge.ID, typeResponse, responseValue, comment, optionalsMedias);
                    }
                    if(response?.status){     
                        let success = this.translateService.instant('CHALLENGE_RESPONSE.MSG_SUCCESS_POST_RESPONSE');
                        if(this.challenge.type === environment.CHALLENGE_TYPE.SALE){
                            success = this.translateService.instant('CHALLENGE_RESPONSE.MSG_SUCCESS_POST_RESPONSE_SALE');
                            if(this.externalLink && this.match){
                                this.nbSalesResponses++;
                                this.checkNbSales();
                            }
                        }                                    
                        const toast = await this.toastController.create({
                            message: success,
                            duration: 6000,
                            position: 'top',
                            color: 'success'
                        });
                        await toast.present();   
                        await this.ambassadorService.refreshAmbassador();  
                        this.responseForm.reset();   
                        this.onSubmitResponse.emit(true);                        
                    }
                    else{
                        let error = this.translateService.instant('CHALLENGE_RESPONSE.MSG_ERROR_POST_RESPONSE');
                        if(response.error === 'response_already_sent'){
                            error = this.translateService.instant('CHALLENGE_RESPONSE.MSG_ERROR_RESPONSE_ALREADY_SENT');
                        }
                        else if (response.error === 'number_of_sales_reached'){
                            error = this.translateService.instant('CHALLENGE_RESPONSE.MSG_ERROR_RESPONSE_NB_SALES_MAX');
                        }
                        const toast = await this.toastController.create({
                            message: error,
                            duration: 6000,
                            position: 'top',
                            color: 'danger'
                        });
                        await toast.present();
                    }
                }
                
                this.isFetchingSubmit = false;
            }
            else{
                for (var controlName in this.responseForm.controls) {
                    if(!this.responseForm.controls[controlName].valid) {
                        this.responseForm.controls[controlName].markAsTouched();
                    }
                }
            }
        }
    }
    
    downloadMedia(url: string){
        if(url && url != ''){
            let ext = url.substr(url.lastIndexOf('.'));
            let link = document.createElement('a');
            link.href = url;
            link.download = 'media_response'+ext;
            link.target = '_blank';
            link.click();
        }
    }
    
    addOptionalMedia(){
        if(this.challenge.optionalsMediasEnabled && this.canPostResponse && !this.disabled){
            this.optionalMediaInput.demoozInputFile.nativeElement.click();
        }
    }
    
    async selectOptionalMedia(optionalMedia: DemoozInputFile|null){       
        if(optionalMedia !== null){
            if(this.challenge.optionalsMediasEnabled && this.canPostResponse && !this.disabled){

                let errorFile: string|undefined = undefined;
                let typeFile = this.getTypeFile(optionalMedia.filedata);
                if(typeFile){
                    let typeResponseInfos = this.settings?.responsesTypes.find((r: any) => r.key === typeFile);
                    if(!this.isValidFileType(optionalMedia.filedata, typeFile)){
                        errorFile = typeResponseInfos.error;
                    }
                    else if(!this.isValidFileSize(optionalMedia.filedata, typeFile)){
                        errorFile = typeResponseInfos.errorSize;                                
                    }
                }
                else{
                    errorFile = this.translateService.instant('CHALLENGE_RESPONSE.MSG_ERROR_FORMAT_FILE');
                }

                if(!errorFile){
                    this.optionalsMedias.push({file: optionalMedia.filedata, type: typeFile});                
                }
                else{
                    const toast = await this.toastController.create({
                        message: errorFile,
                        duration: 6000,
                        position: 'top',
                        color: 'danger'
                    });
                    await toast.present();
                }
            }      
            this.responseForm.controls['optional_media'].setValue(null, {emitEvent: false});  
        }
    }
    
    removeOptionalMedia(index: number){        
        this.optionalsMedias.splice(index, 1);
    }
    
    editResponse(){
        this.disabled = false;
    }
    
    cancel() {
        this.onSubmitResponse.emit(false);
    }
}
